import React, { useEffect, useMemo, useState } from 'react';
import styles from '../BranchDrawer.module.css';
import Ico from '../../common/Ico';
import Pill from '../../common/Pill';
import clx from '../../common/clx';
import { statusLabel, statusTokens, STATUS_KINDS } from '../statusConfig';
import { getUsers, getUserDetails } from '../../../api/auth';
import { updateVulnerabilityAssignee, createGithubIssuesForFindings } from '../../../api/operations';
import { setAlert } from '../../../helpers/alerts';
import Btn from '../../common/Btn';
import ConfirmDialog from '../ConfirmDialog';
import { parseApiDate, timeSinceTS } from '../../../helpers/dates';
import {
  ICO_ALERT,
  ICO_CHECK,
  ICO_CHEVRIGHT,
  ICO_REFRESH,
  ICO_XMARK,
} from './constants';
import { formatDuration, isChangelogOperation, isSkippedStep, isVulnerabilityOperation, mergeOperationSummary, vulnerabilityFindingsAreFinal } from './helpers';
import OutcomeSummary from './OutcomeSummary';
import ChangelogPanel from './ChangelogPanel';
import UserPicker from './UserPicker';
import CheckboxFilter from './CheckboxFilter';

const ICO_CHEVLEFT = 'm15 18-6-6 6-6';
const ICO_COPY = 'M9 9h11a2 2 0 0 1 2 2v11a2 2 0 0 1-2 2H9a2 2 0 0 1-2-2V11a2 2 0 0 1 2-2z M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1';
const ICO_GITHUB = 'M9 19c-5 1.5-5-2.5-7-3m14 6v-3.87a3.37 3.37 0 0 0-.94-2.61c3.14-.35 6.44-1.54 6.44-7A5.44 5.44 0 0 0 20 4.77 5.07 5.07 0 0 0 19.91 1S18.73.65 16 2.48a13.38 13.38 0 0 0-7 0C6.27.65 5.09 1 5.09 1A5.07 5.07 0 0 0 5 4.77a5.44 5.44 0 0 0-1.5 3.78c0 5.42 3.3 6.61 6.44 7A3.37 3.37 0 0 0 9 18.13V22';
const VULN_PAGE_SIZE = 10;
const EXTRA_PACKAGES_RE = /\s(and \d+ more)$/i;

const FIXABLE_LABELS = {
  fixable: 'Fixable',
  partial: 'Partial',
  not_fixable: 'No fix',
  unknown: 'Unknown',
};

const FIXABLE_OPTIONS = Object.entries(FIXABLE_LABELS).map(([value, label]) => ({ value, label }));

const SEVERITY_ORDER = {
  CRITICAL: 0,
  HIGH: 1,
  MEDIUM: 2,
  LOW: 3,
  INFORMATIONAL: 4,
  UNTRIAGED: 5,
  UNKNOWN: 6,
};

const SEVERITY_FILTER_VALUES = ['CRITICAL', 'HIGH', 'MEDIUM', 'LOW'];

const SEVERITY_OPTIONS = SEVERITY_FILTER_VALUES.map((value) => ({
  value,
  label: value.charAt(0) + value.slice(1).toLowerCase(),
}));

const ASSIGNEE_FILTER_OPTIONS = [
  { value: 'all', label: 'All assignees' },
  { value: 'unassigned', label: 'Unassigned' },
];

const ASSIGN_EXTRA_OPTIONS = [{ value: '', label: 'Unassigned' }];

function defaultFixableSelected() {
  return new Set(['fixable', 'partial']);
}

function fixableBucket(value) {
  const normalized = String(value || '').toUpperCase();
  if (normalized === 'YES') return 'fixable';
  if (normalized === 'PARTIAL') return 'partial';
  if (normalized === 'NO') return 'not_fixable';
  return 'unknown';
}

function extraPackagesNote(title) {
  const match = String(title || '').match(EXTRA_PACKAGES_RE);
  return match ? match[1] : null;
}

function versionSummary(finding) {
  const installed = finding.installed_version || 'unknown version';
  const bucket = fixableBucket(finding.fix_available);
  if ((bucket === 'fixable' || bucket === 'partial') && finding.fixed_version) {
    return `${installed} → ${finding.fixed_version}${bucket === 'partial' ? ' (partial)' : ''}`;
  }
  if (bucket === 'not_fixable') return `${installed} · No fix available`;
  return installed;
}

function CopyFindingIdButton({ value }) {
  const [copied, setCopied] = useState(false);
  if (!value) return null;
  const handleCopy = () => {
    navigator.clipboard?.writeText(value);
    setCopied(true);
    setTimeout(() => setCopied(false), 1200);
  };
  return (
    <button
      type="button"
      className={clx(styles.vulnCopyBtn, copied && styles.vulnCopyBtnCopied)}
      onClick={handleCopy}
      title={copied ? 'Copied' : `Copy finding ID · ${value}`}
    >
      <Ico d={copied ? ICO_CHECK : ICO_COPY} size={12} strokeWidth={2} />
    </button>
  );
}

function VulnerabilityFindings({ op, onAssigned }) {
  const [users, setUsers] = useState([]);
  const [currentUser, setCurrentUser] = useState(null);
  const [usersLoading, setUsersLoading] = useState(false);
  const [busyFinding, setBusyFinding] = useState(null);

  const [issueModalOpen, setIssueModalOpen] = useState(false);
  const [issueSelected, setIssueSelected] = useState(() => new Set());
  const [creatingIssues, setCreatingIssues] = useState(false);

  const [severitySelected, setSeveritySelected] = useState(() => new Set());
  const [fixableSelected, setFixableSelected] = useState(defaultFixableSelected);
  const [assigneeFilter, setAssigneeFilter] = useState('all');
  const [page, setPage] = useState(0);

  const findings = useMemo(() => [...(op?.result?.findings || [])].sort((a, b) => {
    const severitySort = (SEVERITY_ORDER[a.severity] ?? 99) - (SEVERITY_ORDER[b.severity] ?? 99);
    if (severitySort !== 0) return severitySort;
    return String(a.title || '').localeCompare(String(b.title || ''));
  }), [op]);
  const fixableCounts = op?.result?.fixable_counts || {};
  const vulnerabilityOperation = isVulnerabilityOperation(op);
  const showDetailsUnavailable = vulnerabilityOperation && !findings.length;

  useEffect(() => {
    if (!vulnerabilityOperation) return;
    setUsersLoading(true);
    Promise.all([getUsers(), getUserDetails()])
      .then(([usersRes, me]) => {
        setUsers(usersRes.users || []);
        setCurrentUser(me);
      })
      .catch((e) => setAlert('Users', e, 'danger'))
      .finally(() => setUsersLoading(false));
  }, [op?.uuid, vulnerabilityOperation]);

  useEffect(() => {
    setSeveritySelected(new Set());
    setFixableSelected(defaultFixableSelected());
    setAssigneeFilter('all');
    setPage(0);
  }, [op?.uuid]);

  const filteredFindings = useMemo(() => findings.filter((finding) => {
    if (severitySelected.size > 0 && !severitySelected.has(finding.severity)) return false;
    if (fixableSelected.size > 0 && !fixableSelected.has(fixableBucket(finding.fix_available))) return false;
    if (assigneeFilter !== 'all') {
      const email = finding.assignment?.assignee_email || '';
      if (assigneeFilter === 'unassigned') { if (email) return false; }
      else if (assigneeFilter === 'me') { if (!currentUser || email !== currentUser.email) return false; }
      else if (email !== assigneeFilter) return false;
    }
    return true;
  }), [findings, severitySelected, fixableSelected, assigneeFilter, currentUser]);

  useEffect(() => { setPage(0); }, [severitySelected, fixableSelected, assigneeFilter]);

  const totalPages = Math.ceil(filteredFindings.length / VULN_PAGE_SIZE) || 0;
  const safePage = totalPages ? Math.min(page, totalPages - 1) : 0;
  const pageFindings = filteredFindings.slice(safePage * VULN_PAGE_SIZE, (safePage + 1) * VULN_PAGE_SIZE);

  useEffect(() => {
    if (page !== safePage) setPage(safePage);
  }, [page, safePage]);

  if (!vulnerabilityOperation) return null;

  const handleAssign = (finding, value) => {
    const assignee = value || null;
    setBusyFinding(finding.finding_key);
    updateVulnerabilityAssignee(op.uuid, finding.finding_key, assignee)
      .then(() => onAssigned?.())
      .then(() => setAlert('Vulnerability assignee', assignee ? 'Assignment updated' : 'Assignment cleared', 'success'))
      .catch((e) => setAlert('Vulnerability assignment error', e, 'danger'))
      .finally(() => setBusyFinding(null));
  };

  const findingsFinal = vulnerabilityFindingsAreFinal(op);
  const issuableFindings = filteredFindings.filter((f) => !f.github_issue_url);

  const openIssueModal = () => {
    setIssueSelected(new Set(issuableFindings.map((f) => f.finding_key)));
    setIssueModalOpen(true);
  };

  const toggleIssueSelected = (findingKey) => {
    setIssueSelected((prev) => {
      const next = new Set(prev);
      if (next.has(findingKey)) next.delete(findingKey);
      else next.add(findingKey);
      return next;
    });
  };

  const handleCreateIssues = () => {
    const keys = [...issueSelected];
    if (!keys.length) return;
    setCreatingIssues(true);
    createGithubIssuesForFindings(op.uuid, keys)
      .then((res) => {
        const results = res?.results || [];
        const created = results.filter((r) => r.status === 'created').length;
        const exists = results.filter((r) => r.status === 'exists').length;
        const failed = results.filter((r) => r.status === 'error');
        if (failed.length) {
          setAlert('GitHub issues', `Created ${created}, ${failed.length} failed: ${failed[0].error}`, 'warning');
        } else {
          setAlert('GitHub issues', `Created ${created} issue${created === 1 ? '' : 's'}${exists ? `, ${exists} already existed` : ''}`, 'success');
        }
        setIssueModalOpen(false);
        return onAssigned?.();
      })
      .catch((e) => setAlert('GitHub issues', e, 'danger'))
      .finally(() => setCreatingIssues(false));
  };

  const filtersActive = severitySelected.size > 0 || fixableSelected.size > 0 || assigneeFilter !== 'all';
  const clearAllFilters = () => {
    setSeveritySelected(new Set());
    setFixableSelected(new Set());
    setAssigneeFilter('all');
  };

  return (
    <div className={styles.sectionGroup}>
      <div className={styles.overlineRow}>
        <span className={styles.overline}>Vulnerabilities</span>
        {findings.length > 0 && (
          <span className={styles.overlineMeta}>
            {filteredFindings.length === findings.length
              ? `${findings.length} findings`
              : `${filteredFindings.length} of ${findings.length} findings`}
          </span>
        )}
        {findingsFinal && findings.length > 0 && (
          <Btn
            size="sm"
            variant="secondary"
            icon={ICO_GITHUB}
            onClick={openIssueModal}
            disabled={issuableFindings.length === 0}
            className={styles.vulnIssueBtn}
          >
            Create GitHub issues
          </Btn>
        )}
      </div>

      {!!Object.keys(fixableCounts).length && (
        <div className={styles.fixableSummary}>
          {Object.entries(FIXABLE_LABELS).map(([key, label]) => (
            <span key={key} className={styles.fixableChip}>
              {label} <strong>{Number(fixableCounts[key] || 0).toLocaleString()}</strong>
            </span>
          ))}
        </div>
      )}

      {showDetailsUnavailable && (
        <div className={styles.empty}>Detailed findings are not available for this scan source.</div>
      )}

      {findings.length > 0 && (
        <>
          <div className={styles.vulnFilterBar}>
            <CheckboxFilter
              label="Severity"
              options={SEVERITY_OPTIONS}
              selected={severitySelected}
              onChange={setSeveritySelected}
            />
            <CheckboxFilter
              label="Fixable"
              options={FIXABLE_OPTIONS}
              selected={fixableSelected}
              onChange={setFixableSelected}
            />
            <UserPicker
              users={users}
              currentUser={currentUser}
              value={assigneeFilter}
              onChange={setAssigneeFilter}
              extraOptions={ASSIGNEE_FILTER_OPTIONS}
              placeholder="Assignee"
              loading={usersLoading}
            />
            {filtersActive && (
              <button type="button" className={styles.filterClearAll} onClick={clearAllFilters}>
                Clear all
              </button>
            )}
          </div>

          {pageFindings.length === 0 ? (
            <div className={styles.empty}>No vulnerabilities match the current filters.</div>
          ) : (
            <div className={styles.vulnList}>
              {pageFindings.map((finding) => {
                const assignment = finding.assignment || {};
                const selected = assignment.assignee_email || '';
                const extraNote = extraPackagesNote(finding.title);
                const noFixAvailable = fixableBucket(finding.fix_available) === 'not_fixable';
                return (
                  <div key={finding.finding_key} className={styles.vulnRow}>
                    <div className={styles.vulnMain}>
                      <div className={styles.vulnTitleRow}>
                        <Pill status={finding.severity === 'CRITICAL' || finding.severity === 'HIGH' ? 'errored' : 'running'} dot={false}>
                          {finding.severity || 'UNKNOWN'}
                        </Pill>
                        {finding.source_url ? (
                          <a
                            className={styles.vulnCveLink}
                            href={finding.source_url}
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            {finding.vulnerability_id || finding.title || 'Vulnerability'}
                          </a>
                        ) : (
                          <span className={styles.vulnCveId}>
                            {finding.vulnerability_id || finding.title || 'Vulnerability'}
                          </span>
                        )}
                        <span className={styles.vulnPackageName} title={finding.package_name || 'Unknown package'}>
                          {finding.package_name || 'Unknown package'}
                        </span>
                        {extraNote && <span className={styles.vulnExtraNote}>{extraNote}</span>}
                      </div>
                      <div className={styles.vulnMetaRow}>
                        <span className={styles.vulnMeta}>
                          {versionSummary(finding)} · Seen {timeSinceTS(finding.last_observed_at)}
                        </span>
                        <CopyFindingIdButton value={finding.id || finding.finding_key} />
                        {finding.github_issue_url && (
                          <a
                            className={styles.vulnIssueLink}
                            href={finding.github_issue_url}
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            <Ico d={ICO_GITHUB} size={12} strokeWidth={2} />
                            Issue
                          </a>
                        )}
                      </div>
                    </div>
                    {noFixAvailable ? (
                      <span className={styles.vulnNoFixBadge}>
                        <Ico d={ICO_CHECK} size={12} strokeWidth={2.6} />
                        No fix available
                      </span>
                    ) : (
                      <UserPicker
                        users={users}
                        currentUser={currentUser}
                        value={selected}
                        onChange={(value) => handleAssign(finding, value)}
                        extraOptions={ASSIGN_EXTRA_OPTIONS}
                        meResolvesToEmail
                        placeholder="Unassigned"
                        disabled={usersLoading || busyFinding === finding.finding_key}
                        loading={usersLoading}
                        align="right"
                        className={styles.vulnAssigneeTrigger}
                      />
                    )}
                  </div>
                );
              })}
            </div>
          )}

          {totalPages > 1 && (
            <div className={styles.vulnPagination}>
              <span className={styles.vulnPaginationText}>
                {safePage * VULN_PAGE_SIZE + 1}–{Math.min((safePage + 1) * VULN_PAGE_SIZE, filteredFindings.length)} of {filteredFindings.length}
              </span>
              <div className={styles.vulnPaginationBtns}>
                <button className={styles.vulnPageBtn} onClick={() => setPage((p) => p - 1)} disabled={safePage === 0} type="button">
                  <Ico d={ICO_CHEVLEFT} size={15} />
                </button>
                <button className={styles.vulnPageBtn} onClick={() => setPage((p) => p + 1)} disabled={safePage + 1 >= totalPages} type="button">
                  <Ico d={ICO_CHEVRIGHT} size={15} />
                </button>
              </div>
            </div>
          )}
        </>
      )}

      <ConfirmDialog
        open={issueModalOpen}
        title="Create GitHub issues"
        message="Pick the findings to file as GitHub issues in the source repository."
        icon={ICO_GITHUB}
        confirmLabel={`Create ${issueSelected.size} issue${issueSelected.size === 1 ? '' : 's'}`}
        confirming={creatingIssues}
        confirmingLabel="Creating…"
        confirmDisabled={issueSelected.size === 0}
        onConfirm={handleCreateIssues}
        onCancel={() => { if (!creatingIssues) setIssueModalOpen(false); }}
      >
        <div className={styles.vulnIssuePicker}>
          {filteredFindings.map((finding) => {
            const already = !!finding.github_issue_url;
            const checked = issueSelected.has(finding.finding_key);
            return (
              <label key={finding.finding_key} className={styles.vulnIssuePickerRow}>
                <input
                  type="checkbox"
                  checked={already ? true : checked}
                  disabled={already || creatingIssues}
                  onChange={() => toggleIssueSelected(finding.finding_key)}
                />
                <Pill status={finding.severity === 'CRITICAL' || finding.severity === 'HIGH' ? 'errored' : 'running'} dot={false}>
                  {finding.severity || 'UNKNOWN'}
                </Pill>
                <span className={styles.vulnIssuePickerCve}>
                  {finding.vulnerability_id || finding.title || 'Vulnerability'}
                </span>
                <span className={styles.vulnIssuePickerPkg}>{finding.package_name || '—'}</span>
                {already && (
                  <a href={finding.github_issue_url} target="_blank" rel="noopener noreferrer" className={styles.vulnIssueLink}>
                    Issue created
                  </a>
                )}
              </label>
            );
          })}
          {filteredFindings.length === 0 && (
            <div className={styles.empty}>No findings match the current filters.</div>
          )}
        </div>
      </ConfirmDialog>
    </div>
  );
}

export default function OperationTasksView({ opData, opLoading, opError, opSummary, onOpenTask, onAssigned }) {
  if (isSkippedStep(opSummary)) return <div className={styles.empty}>Skipped: {opSummary.skipped}</div>;
  if (opLoading) return <div className={styles.loading}>Loading operation…</div>;
  if (opError)   return (
    <div className={styles.errorPanel}>
      <Ico d={ICO_ALERT} size={14} style={{ color: 'var(--error-700)' }} />
      Failed to load operation.
    </div>
  );
  if (!opData) return null;

  const tasks = [...(opData.tasks || [])].sort((a, b) => {
    if (!a.started_at && !b.started_at) return 0;
    if (!a.started_at) return 1;
    if (!b.started_at) return -1;
    return parseApiDate(a.started_at) - parseApiDate(b.started_at);
  });
  const done  = tasks.filter(t => t.status === 'completed').length;
  const summaryOp = mergeOperationSummary(opSummary, opData);

  return (
    <div className={styles.section}>
      <OutcomeSummary op={summaryOp} />
      <VulnerabilityFindings op={summaryOp} onAssigned={onAssigned} />
      {isChangelogOperation(summaryOp) && summaryOp.metadata?.nightly_run_id && (
        <ChangelogPanel runId={summaryOp.metadata.nightly_run_id} status={summaryOp.status} />
      )}

      <div className={styles.sectionGroup}>
        <div className={styles.overlineRow}>
          <span className={styles.overline}>Tasks</span>
          <span className={styles.overlineMeta}>{done}/{tasks.length} completed</span>
        </div>
        <div className={styles.taskList}>
          {tasks.map((task) => {
            const tok = statusTokens(task.status) || STATUS_KINDS.neutral;
            const isRunning  = task.status === 'running';
            const isDone     = task.status === 'completed';
            const isErrored  = task.status === 'errored';
            return (
              <button
                key={task.uuid}
                className={styles.taskRow}
                onClick={() => onOpenTask?.(task)}
                type="button"
              >
                <span className={styles.taskIconWrap} style={{ background: tok.bg, color: tok.fg }}>
                  {isDone   && <Ico d={ICO_CHECK}   size={13} strokeWidth={2.6} />}
                  {isErrored && <Ico d={ICO_XMARK}  size={12} strokeWidth={2.4} />}
                  {isRunning && <Ico d={ICO_REFRESH} size={13} strokeWidth={1.8}
                    style={{ animation: 'hvSpin 1.4s linear infinite' }} />}
                  {!isDone && !isErrored && !isRunning && (
                    <span style={{ width: 8, height: 8, borderRadius: '50%', background: 'currentColor', display: 'block' }} />
                  )}
                </span>
                <span className={styles.taskId}>{task.task_id}</span>
                <span className={styles.taskDur}>{formatDuration(task.started_at, task.completed_at)}</span>
                <Pill status={task.status} dot={false}>{statusLabel(task.status)}</Pill>
                <Ico d={ICO_CHEVRIGHT} size={13} style={{ color: 'var(--gray-400)', flexShrink: 0 }} />
              </button>
            );
          })}
          {tasks.length === 0 && <div className={styles.empty}>No tasks found.</div>}
        </div>
      </div>
    </div>
  );
}
